/**
 * Firebase sync — Firestore-backed Conversation_Store mirror.
 *
 * Why this exists
 * ---------------
 * The browser-side Conversation_Store is per-device. The lock module
 * already gives the same passphrase identical hashes across devices
 * via a deterministic shared salt; this module pushes the user's
 * bundle to Firestore on every write and pulls it on boot, so all
 * devices that authenticate with the same password end up looking at
 * the same conversations.
 *
 * Storage layout (Firestore)
 * --------------------------
 *   userBundles/{userKey} {
 *     bundle:    <ExportBundle>,   // threads + profile + prefs + companionName
 *     updatedAt: <epoch ms>,
 *     lastSeen:  <epoch ms>
 *   }
 *
 * `userKey` is a 64-char SHA-256 hex digest derived from
 *   SHA-256(USER_KEY_SALT || pbkdf2_hash_of_passphrase)
 * The actual passphrase is never sent to Firebase; only the digest is.
 *
 * Lifecycle
 * ---------
 *   - {@link initFirebaseSync} initialises the SDK on first use. Safe
 *     to call repeatedly (idempotent).
 *   - {@link pullAndApply} fetches the saved doc and replaces the
 *     local store via `importAll`. Called once at boot AFTER the
 *     lock route accepts the passphrase.
 *   - {@link installAutoSync} wraps a `ConversationStore` so every
 *     mutation triggers a debounced (3 s) push.
 *   - {@link suspendAutoSync} / {@link resumeAutoSync} let the boot
 *     sequence pause mirroring while the initial pull and slice
 *     re-hydration happen, so the locally-defaulted state cannot
 *     bounce back to the server before the remote bundle has been
 *     applied.
 */

import { initializeApp, type FirebaseApp } from 'firebase/app';
import {
  doc,
  getDoc,
  getFirestore,
  serverTimestamp,
  setDoc,
  type Firestore,
} from 'firebase/firestore';

import type { ExportBundle } from '../domain/types';
import type { ConversationStore } from './store/types';

interface ImportMetaEnvShape {
  VITE_FIREBASE_API_KEY?: string;
  VITE_FIREBASE_AUTH_DOMAIN?: string;
  VITE_FIREBASE_PROJECT_ID?: string;
  VITE_FIREBASE_STORAGE_BUCKET?: string;
  VITE_FIREBASE_MESSAGING_SENDER_ID?: string;
  VITE_FIREBASE_APP_ID?: string;
}

function readEnv(key: keyof ImportMetaEnvShape): string | null {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const env = (import.meta as any)?.env;
    if (env === undefined || env === null) return null;
    const val = env[key];
    return typeof val === 'string' && val.length > 0 ? val : null;
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// SDK initialisation
// ---------------------------------------------------------------------------

let firebaseApp: FirebaseApp | null = null;
let firestore: Firestore | null = null;

/**
 * Returns true when every required Firebase env var is present so the
 * SDK can actually be initialised. Used by callers to gate UI / boot
 * pulls without making the entire app crash if the keys are missing.
 */
export function isSyncEnabled(): boolean {
  return (
    readEnv('VITE_FIREBASE_API_KEY') !== null &&
    readEnv('VITE_FIREBASE_PROJECT_ID') !== null &&
    readEnv('VITE_FIREBASE_APP_ID') !== null
  );
}

function getFirestoreInstance(): Firestore | null {
  if (firestore !== null) return firestore;
  if (!isSyncEnabled()) {
    // eslint-disable-next-line no-console
    console.warn(
      '[sync] disabled — VITE_FIREBASE_* env vars missing in build',
      {
        hasApiKey: readEnv('VITE_FIREBASE_API_KEY') !== null,
        hasProjectId: readEnv('VITE_FIREBASE_PROJECT_ID') !== null,
        hasAppId: readEnv('VITE_FIREBASE_APP_ID') !== null,
      },
    );
    return null;
  }

  const config = {
    apiKey: readEnv('VITE_FIREBASE_API_KEY') ?? '',
    authDomain: readEnv('VITE_FIREBASE_AUTH_DOMAIN') ?? '',
    projectId: readEnv('VITE_FIREBASE_PROJECT_ID') ?? '',
    storageBucket: readEnv('VITE_FIREBASE_STORAGE_BUCKET') ?? '',
    messagingSenderId: readEnv('VITE_FIREBASE_MESSAGING_SENDER_ID') ?? '',
    appId: readEnv('VITE_FIREBASE_APP_ID') ?? '',
  };

  try {
    firebaseApp = initializeApp(config);
    firestore = getFirestore(firebaseApp);
    // eslint-disable-next-line no-console
    console.info('[sync] firestore ready', { projectId: config.projectId });
    return firestore;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('[sync] firebase init failed:', err);
    return null;
  }
}

// ---------------------------------------------------------------------------
// User key derivation (mirrors src/infra/lock.ts hashing)
// ---------------------------------------------------------------------------

const USER_KEY_SALT = new TextEncoder().encode('companion-chat:user-key:v1');
const HASH_KEY = 'companion.lock.hash';

/**
 * Derive a 64-char SHA-256 hex digest from the locally-stored PBKDF2
 * password hash + a public app-wide salt. This is the Firestore
 * document id used to identify the user.
 */
async function deriveUserKey(): Promise<string | null> {
  if (typeof localStorage === 'undefined') return null;
  const hashB64 = localStorage.getItem(HASH_KEY);
  if (hashB64 === null || hashB64.length === 0) return null;

  const binary = atob(hashB64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);

  const concat = new Uint8Array(USER_KEY_SALT.length + bytes.length);
  concat.set(USER_KEY_SALT, 0);
  concat.set(bytes, USER_KEY_SALT.length);

  const digest = await crypto.subtle.digest('SHA-256', concat);
  const view = new Uint8Array(digest);
  let hex = '';
  for (let i = 0; i < view.length; i++) {
    hex += (view[i] ?? 0).toString(16).padStart(2, '0');
  }
  return hex;
}

// ---------------------------------------------------------------------------
// Pull / push primitives
// ---------------------------------------------------------------------------

interface BundleDoc {
  bundle: ExportBundle;
  updatedAt: number;
}

/**
 * Fetch the user's saved bundle from Firestore. Returns:
 *   - `null`             when sync is disabled / no PIN / no SDK / fetch error.
 *   - `{ bundle: null }` when the doc does not exist yet.
 *   - `{ bundle: ... }`  otherwise.
 */
export async function pullFromServer(): Promise<{
  bundle: ExportBundle | null;
  updatedAt: number | null;
} | null> {
  const db = getFirestoreInstance();
  if (db === null) return null;
  const userKey = await deriveUserKey();
  if (userKey === null) return null;

  try {
    const ref = doc(db, 'userBundles', userKey);
    const snap = await getDoc(ref);
    if (!snap.exists()) {
      return { bundle: null, updatedAt: null };
    }
    const data = snap.data() as Partial<BundleDoc>;
    if (data.bundle === undefined || data.bundle === null) {
      return { bundle: null, updatedAt: null };
    }
    return {
      bundle: data.bundle,
      updatedAt: typeof data.updatedAt === 'number' ? data.updatedAt : null,
    };
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[sync] pull failed:', err);
    return null;
  }
}

/**
 * Push the local store's current bundle to Firestore. Errors are
 * swallowed — sync is best-effort, the local store remains the
 * source of truth on this device.
 */
export async function pushToServer(store: ConversationStore): Promise<boolean> {
  const db = getFirestoreInstance();
  if (db === null) return false;
  const userKey = await deriveUserKey();
  if (userKey === null) return false;

  try {
    const bundle = await store.exportAll();
    const ref = doc(db, 'userBundles', userKey);
    await setDoc(ref, {
      bundle,
      updatedAt: Date.now(),
      lastSeen: serverTimestamp(),
    });
    return true;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[sync] push failed:', err);
    return false;
  }
}

/**
 * Pull the user's bundle and replace the local store's contents.
 *
 * Invoked from the boot sequence AFTER the lock route has accepted
 * the passphrase (so the PBKDF2 hash exists in localStorage). The
 * local store's `importAll` validates the bundle and runs the swap
 * inside a single transaction.
 *
 * Returns:
 *   - `'applied'` — the server had a bundle and we imported it.
 *   - `'empty'`   — the server had nothing for this user yet.
 *   - `'skipped'` — sync disabled, no user key, or fetch failed.
 */
export async function pullAndApply(
  store: ConversationStore,
): Promise<'applied' | 'empty' | 'skipped'> {
  const result = await pullFromServer();
  if (result === null) return 'skipped';
  if (result.bundle === null) return 'empty';
  try {
    const importResult = await store.importAll(result.bundle);
    return importResult.ok ? 'applied' : 'skipped';
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[sync] importAll failed:', err);
    return 'skipped';
  }
}

// ---------------------------------------------------------------------------
// Auto-sync wrapper
// ---------------------------------------------------------------------------

/**
 * Auto-sync starts SUSPENDED so the boot sequence's initial pull +
 * hydrate cannot trigger a stray push that would overwrite the
 * freshly-pulled remote bundle. Call {@link resumeAutoSync} once boot
 * is complete.
 */
let autoSyncSuspended = true;

export function resumeAutoSync(): void {
  autoSyncSuspended = false;
}

export function suspendAutoSync(): void {
  autoSyncSuspended = true;
}

/**
 * Wrap a `ConversationStore` so every successful write triggers a
 * debounced push to Firestore. Transparent forward — every method
 * call goes to the underlying store; only the listed mutating
 * methods schedule a push afterwards.
 *
 * When sync is disabled (no Firebase keys) the function returns the
 * store unchanged so there is zero runtime cost.
 */
export function installAutoSync(store: ConversationStore): ConversationStore {
  if (!isSyncEnabled()) return store;

  // Eagerly initialise the SDK now so the first push doesn't pay the
  // init cost while the user is waiting for a save to complete.
  getFirestoreInstance();

  const DEBOUNCE_MS = 3000;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let inFlight = false;

  const schedulePush = (): void => {
    if (autoSyncSuspended) return;
    if (timer !== null) clearTimeout(timer);
    timer = setTimeout(() => {
      timer = null;
      if (inFlight) {
        // Re-arm so we don't drop the latest write while a push is
        // already running.
        schedulePush();
        return;
      }
      inFlight = true;
      void pushToServer(store).finally(() => {
        inFlight = false;
      });
    }, DEBOUNCE_MS);
  };

  // Final-flush on tab close: best effort, fire-and-forget. Firebase
  // SDK handles its own internal queue, so even a partial network
  // hiccup is recoverable on the next boot.
  if (typeof window !== 'undefined') {
    window.addEventListener('pagehide', () => {
      if (autoSyncSuspended) return;
      void pushToServer(store);
    });
  }

  const writeMethods = new Set<keyof ConversationStore>([
    'createThread',
    'renameThread',
    'deleteThread',
    'clearThread',
    'appendMessage',
    'updateMessage',
    'setRollingSummary',
    'setProfile',
    'setPreferences',
    'setCompanionName',
    'setApiKey',
    'enqueueMessage',
    'removeQueuedMessage',
    'updateQueuedMessage',
    'importAll',
    'wipeAll',
  ]);

  return new Proxy(store, {
    get(target, prop, receiver) {
      const orig = Reflect.get(target, prop, receiver);
      if (typeof orig !== 'function') return orig;
      const fn = orig as (...args: unknown[]) => unknown;
      const key = prop as keyof ConversationStore;
      if (!writeMethods.has(key)) {
        return fn.bind(target);
      }
      return (...args: unknown[]): unknown => {
        const result = fn.apply(target, args);
        if (result instanceof Promise) {
          return result.then((value) => {
            schedulePush();
            return value;
          });
        }
        schedulePush();
        return result;
      };
    },
  });
}
